// POS → Restaurant → Configuración (/pos/rest-config). Por LOCAL:
// 10% de servicio (cargo legal, se factura como línea sin IVA — propina legal
// excluida de la base) y umbrales de color del KDS. Solo administradores.
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { SelectorLocal, Campo, Switch, fieldCls, posRpc } from './shared';
import { RestEstadoT, restEstadoQuery, useLocalRestaurant } from './restaurante';

export function ConfigRestaurant() {
  const qc = useQueryClient();
  const { locationId, setLocationId, locations } = useLocalRestaurant();
  const { data: estado } = useQuery<RestEstadoT>(restEstadoQuery(locationId));

  const [cobra, setCobra] = useState(false);
  const [pct, setPct] = useState('10');
  const [verde, setVerde] = useState('5');
  const [amarillo, setAmarillo] = useState('8');
  const [autoservicio, setAutoservicio] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const c = estado?.config;
    setCobra(c?.cobra_servicio ?? false);
    setPct(String(c?.servicio_pct ?? 10));
    setVerde(String(c?.kds_verde_min ?? 5));
    setAmarillo(String(c?.kds_amarillo_min ?? 8));
    setAutoservicio(c?.qr_autoservicio ?? false);
  }, [estado?.config]);

  const guardar = async () => {
    setGuardando(true);
    try {
      await posRpc('rest_config_guardar', {
        _location: locationId,
        _data: { cobra_servicio: cobra, servicio_pct: Number(pct) || 10,
          kds_verde_min: Number(verde) || 5, kds_amarillo_min: Number(amarillo) || 8,
          qr_autoservicio: autoservicio },
      });
      void qc.invalidateQueries({ queryKey: ['rest-estado'] });
      toast.success('Configuración Restaurant guardada');
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setGuardando(false); }
  };

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Settings2 className="w-5 h-5" /> Configuración Restaurant</h1>
        <SelectorLocal locationId={locationId} setLocationId={setLocationId} locations={locations} />
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">10% de servicio (cargo legal)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Switch checked={cobra} onChange={setCobra}
                  label="Este local cobra el servicio (establecimientos de 1ª/2ª categoría)" />
          <Campo label="Porcentaje">
            <input className={`${fieldCls} !w-28 text-right`} type="number" min="0" max="20" step="0.5"
                   value={pct} onChange={(e) => setPct(e.target.value)} disabled={!cobra} />
          </Campo>
          <p className="text-xs text-muted-foreground">
            Se congela en cada cuenta al abrirla y se factura como la línea «Incluye {pct || 10}% de
            servicios-propina-TIP» SIN IVA (la propina legal no forma parte de la base imponible).
            Recuerda su reparto mensual entre los trabajadores. La propina VOLUNTARIA es aparte
            (campo del cobro; solo arqueo, no factura).
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Kitchen Display — umbrales de color</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <Campo label="Verde hasta (minutos)">
              <input className={`${fieldCls} text-right`} type="number" min="1" value={verde} onChange={(e) => setVerde(e.target.value)} />
            </Campo>
            <Campo label="Amarillo hasta (minutos)">
              <input className={`${fieldCls} text-right`} type="number" min="2" value={amarillo} onChange={(e) => setAmarillo(e.target.value)} />
            </Campo>
          </div>
          <p className="text-xs text-muted-foreground">
            Pasado el amarillo, el ticket se pinta ROJO y parpadea. Un fast-casual suele usar 5/8 min;
            un restaurante de mantel, 12/20.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Pedido por QR de mesa</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Switch checked={autoservicio} onChange={setAutoservicio}
                  label="Autoservicio: el cliente puede ABRIR su mesa desde el QR (sin esperar al mesero)" />
          <p className="text-xs text-muted-foreground">
            Con autoservicio ACTIVO, quien escanea el QR de una mesa libre puede abrir la cuenta
            él mismo (indica cuántos son) y ordena de inmediato; el mesero solo confirma los platos
            antes de que salgan a cocina. Con autoservicio INACTIVO se mantiene el flujo normal:
            el mesero sienta primero (Salón → abrir cuenta con su PIN) y recién ahí el QR deja ordenar.
            El precio siempre lo pone el sistema, nunca el teléfono del cliente.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => void guardar()} isLoading={guardando}><Save className="w-4 h-4 mr-1" /> Guardar</Button>
      </div>
    </div>
  );
}
